/**
 * Zendesk Web Widget (Classic) lazy-load state machine — COM-183.
 *
 * Owns the `#ze-snippet` script injection that used to live in
 * `ZendeskSnippetScript` (SFCC-11018 / ANALYTICS-30), but *nothing* is fetched
 * from zdassets until `load()` is called — the facade component
 * (`zendesk-chat-facade.tsx`) calls it on first facade click or on a
 * post-first-interaction idle slot. Before that, zero widget bytes hit the
 * network, which is the whole point: the ~463KB classic widget bundle no
 * longer competes with hydration / LCP on every page view.
 *
 * States: `idle` → `loading` → `ready` (or `error`, which is retryable — a
 * later `load()` re-injects). "ready" means the widget framework has *booted*,
 * not merely that snippet.js parsed: the classic snippet defines `zE` as a
 * command queue synchronously, and `zE(callback)` runs the callback once the
 * main widget bundle is up. We wait for that so the facade only hands off to
 * the real launcher when the real launcher exists.
 *
 * Framework-free and DOM-touching only inside methods, so it is SSR-import
 * safe and unit-testable without React.
 */

/** DOM id Zendesk's docs mandate for the snippet script tag. Also the dedup
 * key (kept from ZendeskSnippetScript, and referenced by the CSP comment in
 * entry.server.tsx). */
export const ZENDESK_SNIPPET_SCRIPT_ID = "ze-snippet";

/**
 * Window event that requests the chat widget be loaded + opened. Fired by UI
 * that wants to open chat without owning the loader (footer "Live Chat"
 * trigger in the `icon-links-grid` Builder block); handled by the mounted
 * facade. No listener (chat disabled / WDH / TPA) ⇒ no-op, matching the old
 * `if (window.zE)` guard's behavior.
 */
export const ZENDESK_OPEN_EVENT = "hec:zendesk-open";

/** Iframe id of the classic widget's floating launcher (bottom-right pill). */
export const ZENDESK_LAUNCHER_IFRAME_ID = "launcher";

/** Iframe id of the classic widget's chat window. Visible while open (the
 * launcher iframe is `visibility: hidden` during that time — verified live). */
export const ZENDESK_WIDGET_IFRAME_ID = "webWidget";

/** Classic snippet's `zE` global: a command queue function; `activate()` is
 * the legacy API that exists once the widget has booted. */
type ZendeskCommandApi = ((...args: unknown[]) => void) & {
  activate?: () => void;
};

export type ZendeskLoadState = "idle" | "loading" | "ready" | "error";

export interface ZendeskWidgetLoader {
  getState(): ZendeskLoadState;
  /** Subscribe to state changes. Returns an unsubscribe function. */
  subscribe(listener: (state: ZendeskLoadState) => void): () => void;
  /**
   * Inject the snippet (idempotent — a second call while loading/ready never
   * injects twice). `openOnReady` preserves click intent: the widget is opened
   * programmatically as soon as it boots (or immediately if already ready).
   * Calling from `error` retries the injection.
   */
  load(options?: { openOnReady?: boolean }): void;
}

function getZE(): ZendeskCommandApi | undefined {
  const zE = (window as { zE?: unknown }).zE;
  return typeof zE === "function" ? (zE as ZendeskCommandApi) : undefined;
}

/** Open the widget. `zE('webWidget', 'open')` is the classic-widget command
 * (queueable pre-boot); `zE.activate()` is the legacy fallback the footer
 * trigger historically used. */
function openWidget(): void {
  const zE = getZE();
  if (!zE) return;
  try {
    zE("webWidget", "open");
  } catch {
    try {
      zE.activate?.();
    } catch {
      // Widget refused to open — nothing else to do; the real launcher is
      // rendered at this point so the shopper still has an entry point.
    }
  }
}

/**
 * Hide the whole real widget (launcher + chat window) via
 * `zE('webWidget', 'hide')` — ET-629. For a widget that already loaded on an
 * earlier page and survives a client-side navigation into a route where chat
 * must not show (`/intake/*`). Returns whether the command was issued: `false`
 * when `zE` doesn't exist yet (widget never loaded, so there is nothing to
 * hide). Like `open`, the command is queueable pre-boot.
 */
export function hideWidget(): boolean {
  const zE = getZE();
  if (!zE) return false;
  try {
    zE("webWidget", "hide");
    return true;
  } catch {
    return false;
  }
}

/** Undo {@link hideWidget} (`zE('webWidget', 'show')`). Only call after a
 * successful hide — `show` would also override an account-side decision to
 * keep the launcher hidden. */
export function showWidget(): void {
  try {
    getZE()?.("webWidget", "show");
  } catch {
    // Nothing to recover — the widget stays hidden until the next page load.
  }
}

function isIframeShowing(id: string): boolean {
  const el = document.getElementById(id);
  if (!(el instanceof HTMLIFrameElement)) return false;
  const cs = getComputedStyle(el);
  // The launcher iframe is inserted `opacity: 0` and fades in over 250ms;
  // "showing" means the fade has (effectively) finished. An unparseable /
  // empty opacity is treated as visible (fail open — never strand the poll).
  const opacity = cs.opacity === "" ? 1 : Number.parseFloat(cs.opacity);
  return (
    cs.display !== "none" && cs.visibility !== "hidden" && !(opacity < 0.99)
  );
}

/**
 * Whether the REAL widget has painted an entry point in the corner: either the
 * floating launcher iframe or the (open) chat-window iframe is actually
 * visible. Measured live: the `zE(callback)` boot signal fires ~550ms *before*
 * the launcher iframe finishes its width/opacity reveal — unmounting the
 * facade at boot leaves a launcher-less corner for that window, so the facade
 * waits for this instead.
 */
export function isRealWidgetPainted(): boolean {
  return (
    isIframeShowing(ZENDESK_LAUNCHER_IFRAME_ID) ||
    isIframeShowing(ZENDESK_WIDGET_IFRAME_ID)
  );
}

/**
 * Invoke `onPainted` once {@link isRealWidgetPainted} turns true (checked
 * immediately, then polled), or after `maxWaitMs` as a fail-safe — e.g. a page
 * that suppresses the launcher via `zESettings` would otherwise never hand
 * off. Returns a cancel function.
 */
export function watchForWidgetPaint(
  onPainted: () => void,
  {
    pollMs = 100,
    maxWaitMs = 4000,
  }: { pollMs?: number; maxWaitMs?: number } = {},
): () => void {
  if (isRealWidgetPainted()) {
    onPainted();
    return () => {};
  }
  const deadline = Date.now() + maxWaitMs;
  const interval = window.setInterval(() => {
    if (isRealWidgetPainted() || Date.now() >= deadline) {
      window.clearInterval(interval);
      onPainted();
    }
  }, pollMs);
  return () => window.clearInterval(interval);
}

export function createZendeskWidgetLoader({
  src,
  nonce,
}: {
  src: string;
  nonce?: string;
}): ZendeskWidgetLoader {
  let state: ZendeskLoadState = "idle";
  let openOnReady = false;
  const listeners = new Set<(state: ZendeskLoadState) => void>();

  const setState = (next: ZendeskLoadState) => {
    if (state === next) return;
    state = next;
    for (const listener of listeners) listener(next);
  };

  const handleBooted = () => {
    setState("ready");
    if (openOnReady) {
      openOnReady = false;
      openWidget();
    }
  };

  // snippet.js has executed: `zE` (the command queue) now exists. Ask the
  // framework to tell us when it has actually booted so the facade → real
  // launcher swap is seamless. If `zE` is unexpectedly absent (snippet
  // contract change), fail open and treat script-load as ready.
  const handleScriptLoaded = () => {
    const zE = getZE();
    if (zE) {
      try {
        zE(handleBooted);
        return;
      } catch {
        // fall through to fail-open below
      }
    }
    handleBooted();
  };

  const handleScriptError = (script: HTMLScriptElement) => {
    // Remove the dead node so a retry can re-inject under the mandated id.
    script.remove();
    // The click intent dies with the failed load: the facade re-arms to its
    // idle look, so a LATER background (idle) load must not surprise-open the
    // chat window off a stale intent. A retry click re-records it.
    openOnReady = false;
    setState("error");
  };

  const load: ZendeskWidgetLoader["load"] = options => {
    if (options?.openOnReady) openOnReady = true;

    if (state === "ready") {
      if (openOnReady) {
        openOnReady = false;
        openWidget();
      }
      return;
    }
    if (state === "loading") return; // single-flight; intent already recorded

    // Defensive: if some other code injected the snippet (nothing does today),
    // don't double-inject — attach to the existing element's lifecycle.
    const existing = document.getElementById(
      ZENDESK_SNIPPET_SCRIPT_ID,
    ) as HTMLScriptElement | null;
    if (existing) {
      setState("loading");
      if (getZE()) {
        handleScriptLoaded();
      } else {
        existing.addEventListener("load", handleScriptLoaded, { once: true });
        existing.addEventListener("error", () => handleScriptError(existing), {
          once: true,
        });
      }
      return;
    }

    setState("loading");
    const script = document.createElement("script");
    script.id = ZENDESK_SNIPPET_SCRIPT_ID;
    script.src = src;
    // `async` keeps even the on-demand load off any parser-blocking path.
    script.async = true;
    if (nonce) script.nonce = nonce;
    script.addEventListener("load", handleScriptLoaded, { once: true });
    script.addEventListener("error", () => handleScriptError(script), {
      once: true,
    });
    document.head.appendChild(script);
  };

  return {
    getState: () => state,
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    load,
  };
}
